import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "playBtn", "playIcon", "stopIcon", "statusText", "timeDisplay",
    "volume", "volumeLabel",
    "eqLow", "eqLowLabel",
    "eqMid", "eqMidLabel",
    "eqHigh", "eqHighLabel",
    "loop"
  ]

  static values = {
    audioUrl: String
  }

  FADE_DURATION = 2

  connect() {
    this.audioContext = null
    this.audioGraph = null
    this.audio = null
    this.playing = false
    this.animFrame = null
    this.fadeTimer = null
    this._fadeStopTimer = null
    this._endedHandler = null

    if (this.audioUrlValue) {
      this.audio = new Audio()
      this.audio.preload = "auto"
      this.audio.src = this.audioUrlValue
    }
  }

  disconnect() {
    this._stopImmediate()
    if (this.audio) {
      this.audio.pause()
      this.audio.removeAttribute("src")
      this.audio.load()
    }
    if (this.audioContext) this.audioContext.close()
  }

  _getOrCreateContext() {
    if (!this.audioContext || this.audioContext.state === "closed") {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    }
    if (this.audioContext.state === "suspended") {
      this.audioContext.resume()
    }
    return this.audioContext
  }

  _ensureAudioGraph(ctx) {
    if (this.audioGraph) return this.audioGraph
    if (!this.audio) return null

    const mediaSource = ctx.createMediaElementSource(this.audio)

    const gainNode = ctx.createGain()
    const eqLow = ctx.createBiquadFilter()
    eqLow.type = "lowshelf"
    eqLow.frequency.value = 320

    const eqMid = ctx.createBiquadFilter()
    eqMid.type = "peaking"
    eqMid.frequency.value = 1000
    eqMid.Q.value = 0.5

    const eqHigh = ctx.createBiquadFilter()
    eqHigh.type = "highshelf"
    eqHigh.frequency.value = 3200

    mediaSource.connect(gainNode)
    gainNode.connect(eqLow)
    eqLow.connect(eqMid)
    eqMid.connect(eqHigh)
    eqHigh.connect(ctx.destination)

    this.audioGraph = { gainNode, eqLow, eqMid, eqHigh }
    return this.audioGraph
  }

  async togglePlay() {
    const ctx = this._getOrCreateContext()

    if (this.playing) {
      this.stop()
    } else {
      await this.play(ctx)
    }
  }

  async play(ctx) {
    if (!this.audio) return
    if (!ctx) ctx = this._getOrCreateContext()

    if (this._fadeStopTimer) {
      clearTimeout(this._fadeStopTimer)
      this._fadeStopTimer = null
    }

    if (this._endedHandler) {
      this.audio.removeEventListener("ended", this._endedHandler)
      this._endedHandler = null
    }

    const graph = this._ensureAudioGraph(ctx)
    if (!graph) return

    graph.gainNode.gain.cancelScheduledValues(ctx.currentTime)
    graph.gainNode.gain.value = this._volumeValue()
    graph.eqLow.gain.value = this._eqValue("Low")
    graph.eqMid.gain.value = this._eqValue("Mid")
    graph.eqHigh.gain.value = this._eqValue("High")

    const isLooping = this.hasLoopTarget ? this.loopTarget.checked : false
    this.audio.loop = isLooping
    this.audio.currentTime = 0

    try {
      await this.audio.play()
    } catch (e) {
      console.error("Failed to play preview:", e)
      return
    }

    this.playing = true
    this._updateUI()
    this._tickTime()

    if (!isLooping && isFinite(this.audio.duration) && this.audio.duration > this.FADE_DURATION) {
      const fadeAt = (this.audio.duration - this.FADE_DURATION) * 1000
      this.fadeTimer = setTimeout(() => {
        if (this.playing && this.audioGraph) {
          const now = ctx.currentTime
          graph.gainNode.gain.setValueAtTime(graph.gainNode.gain.value, now)
          graph.gainNode.gain.linearRampToValueAtTime(0, now + this.FADE_DURATION)
        }
      }, fadeAt)
    }

    this._endedHandler = () => {
      this.audio.removeEventListener("ended", this._endedHandler)
      this._endedHandler = null
      if (this.fadeTimer) { clearTimeout(this.fadeTimer); this.fadeTimer = null }
      this.playing = false
      this._updateUI()
      if (this.animFrame) cancelAnimationFrame(this.animFrame)
      if (this.hasTimeDisplayTarget) this.timeDisplayTarget.textContent = "0:00"
    }
    this.audio.addEventListener("ended", this._endedHandler)
  }

  stop() {
    if (!this.audio || !this.playing) return

    if (this.fadeTimer) { clearTimeout(this.fadeTimer); this.fadeTimer = null }
    if (this._endedHandler) {
      this.audio.removeEventListener("ended", this._endedHandler)
      this._endedHandler = null
    }

    this.playing = false
    this._updateUI()

    const ctx = this.audioContext
    const graph = this.audioGraph

    if (ctx && graph) {
      const now = ctx.currentTime
      graph.gainNode.gain.cancelScheduledValues(now)
      graph.gainNode.gain.setValueAtTime(graph.gainNode.gain.value, now)
      graph.gainNode.gain.linearRampToValueAtTime(0, now + this.FADE_DURATION)

      this._fadeStopTimer = setTimeout(() => {
        this.audio.pause()
        this.audio.currentTime = 0
        this._fadeStopTimer = null
        if (this.animFrame) cancelAnimationFrame(this.animFrame)
        if (this.hasTimeDisplayTarget) this.timeDisplayTarget.textContent = "0:00"
      }, this.FADE_DURATION * 1000 + 50)
    } else {
      this.audio.pause()
      this.audio.currentTime = 0
      if (this.animFrame) cancelAnimationFrame(this.animFrame)
      if (this.hasTimeDisplayTarget) this.timeDisplayTarget.textContent = "0:00"
    }
  }

  _stopImmediate() {
    if (this.fadeTimer) { clearTimeout(this.fadeTimer); this.fadeTimer = null }
    if (this._fadeStopTimer) { clearTimeout(this._fadeStopTimer); this._fadeStopTimer = null }
    if (this._endedHandler && this.audio) {
      this.audio.removeEventListener("ended", this._endedHandler)
      this._endedHandler = null
    }
    if (this.animFrame) { cancelAnimationFrame(this.animFrame); this.animFrame = null }

    this.playing = false
    if (this.audio) {
      try { this.audio.pause() } catch (_) {}
      this.audio.currentTime = 0
    }
  }

  updateVolume() {
    const val = parseFloat(this.volumeTarget.value)
    this.volumeLabelTarget.textContent = `${Math.round(val * 100)}%`
    if (this.audioGraph) {
      this.audioGraph.gainNode.gain.value = val
    }
  }

  updateEq() {
    if (this.hasEqLowTarget) {
      this.eqLowLabelTarget.textContent = `${this.eqLowTarget.value} dB`
      if (this.audioGraph) this.audioGraph.eqLow.gain.value = parseFloat(this.eqLowTarget.value)
    }
    if (this.hasEqMidTarget) {
      this.eqMidLabelTarget.textContent = `${this.eqMidTarget.value} dB`
      if (this.audioGraph) this.audioGraph.eqMid.gain.value = parseFloat(this.eqMidTarget.value)
    }
    if (this.hasEqHighTarget) {
      this.eqHighLabelTarget.textContent = `${this.eqHighTarget.value} dB`
      if (this.audioGraph) this.audioGraph.eqHigh.gain.value = parseFloat(this.eqHighTarget.value)
    }
  }

  _volumeValue() {
    if (this.hasVolumeTarget) return parseFloat(this.volumeTarget.value)
    return 0.8
  }

  _eqValue(band) {
    const target = `eq${band}Target`
    const has = `hasEq${band}Target`
    if (this[has]) return parseFloat(this[target].value)
    return 0
  }

  _updateUI() {
    if (this.hasPlayIconTarget && this.hasStopIconTarget) {
      this.playIconTarget.classList.toggle("hidden", this.playing)
      this.stopIconTarget.classList.toggle("hidden", !this.playing)
    }
    if (this.hasPlayBtnTarget) {
      if (this.playing) {
        this.playBtnTarget.classList.remove("bg-indigo-600", "hover:bg-indigo-700")
        this.playBtnTarget.classList.add("bg-red-600", "hover:bg-red-700")
      } else {
        this.playBtnTarget.classList.remove("bg-red-600", "hover:bg-red-700")
        this.playBtnTarget.classList.add("bg-indigo-600", "hover:bg-indigo-700")
      }
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = this.playing ? "Playing..." : "Preview"
    }
  }

  _tickTime() {
    if (!this.hasTimeDisplayTarget || !this.playing || !this.audio) return

    const elapsed = this.audio.currentTime
    const minutes = Math.floor(elapsed / 60)
    const seconds = Math.floor(elapsed % 60)
    this.timeDisplayTarget.textContent = `${minutes}:${seconds.toString().padStart(2, "0")}`

    this.animFrame = requestAnimationFrame(() => this._tickTime())
  }
}
